import React, { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

function Edge({ start, end, color, intensity = 1 }) {
  const lineRef = useRef()
  const pulseRef = useRef()
  const particlesRef = useRef()
  
  // Create a curved path for the synapse
  const curve = React.useMemo(() => {
    const startVec = new THREE.Vector3(...start)
    const endVec = new THREE.Vector3(...end)
    const midPoint = new THREE.Vector3().addVectors(startVec, endVec).multiplyScalar(0.5)
    
    // Add some random offset to the midpoint for more organic curves
    const offset = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 2 + 1,
      (Math.random() - 0.5) * 2
    )
    midPoint.add(offset)
    
    return new THREE.QuadraticBezierCurve3(startVec, midPoint, endVec)
  }, [start, end])

  // Create particle system for energy flow
  const particleGeometry = React.useMemo(() => {
    const particleCount = 30 // Increased particle count
    const positions = new Float32Array(particleCount * 3)
    const speeds = new Float32Array(particleCount)
    const offsets = new Float32Array(particleCount)
    const sizes = new Float32Array(particleCount)
    
    for (let i = 0; i < particleCount; i++) {
      const t = i / particleCount
      const point = curve.getPoint(t)
      positions[i * 3] = point.x
      positions[i * 3 + 1] = point.y
      positions[i * 3 + 2] = point.z
      speeds[i] = Math.random() * 0.5 + 0.5
      offsets[i] = Math.random() * Math.PI * 2
      sizes[i] = Math.random() * 0.3 + 0.2 // Larger particles
    }
    
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('speed', new THREE.BufferAttribute(speeds, 1))
    geometry.setAttribute('offset', new THREE.BufferAttribute(offsets, 1))
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))
    return geometry
  }, [curve])

  // Enhanced shader material for the main synapse
  const synapseMaterial = React.useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(color) },
        intensity: { value: intensity }
      },
      vertexShader: `
        uniform float time;
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float time;
        uniform float intensity;
        varying vec3 vPosition;
        
        void main() {
          float dist = length(vPosition);
          float wave = sin(dist * 10.0 - time * 5.0) * 0.5 + 0.5;
          float pulse = intensity * (0.6 + wave * 0.4); // Increased base opacity
          vec3 finalColor = mix(color, vec3(1.0), wave * 0.4); // More pronounced color variation
          gl_FragColor = vec4(finalColor, pulse * 0.7); // Increased overall opacity
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending
    })
  }, [color, intensity])

  // Enhanced particle material
  const particleMaterial = React.useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(color) },
        intensity: { value: intensity }
      },
      vertexShader: `
        attribute float speed;
        attribute float offset;
        attribute float size;
        uniform float time;
        varying float vAlpha;
        
        void main() {
          vAlpha = sin(time * speed + offset) * 0.5 + 0.5;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = size * (300.0 / -mvPosition.z); // Dynamic size based on distance
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float intensity;
        varying float vAlpha;
        
        void main() {
          float r = length(gl_PointCoord - vec2(0.5));
          if (r > 0.5) discard;
          float glow = smoothstep(0.5, 0.0, r);
          vec3 finalColor = mix(color, vec3(1.0), glow * 0.5); // Enhanced glow effect
          gl_FragColor = vec4(finalColor, vAlpha * intensity * glow * 0.8); // Increased particle visibility
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
  }, [color, intensity])

  // Animation
  useFrame((state) => {
    const t = state.clock.getElapsedTime()
    
    if (lineRef.current) {
      // Update main synapse
      lineRef.current.material.uniforms.time.value = t
      
      // Update particles
      if (particlesRef.current) {
        particlesRef.current.material.uniforms.time.value = t
        
        // Move particles along the curve
        const positions = particlesRef.current.geometry.attributes.position.array
        const speeds = particlesRef.current.geometry.attributes.speed.array
        
        for (let i = 0; i < positions.length; i += 3) {
          const speed = speeds[i / 3]
          let progress = (t * speed) % 1
          const point = curve.getPoint(progress)
          positions[i] = point.x
          positions[i + 1] = point.y
          positions[i + 2] = point.z
        }
        
        particlesRef.current.geometry.attributes.position.needsUpdate = true
      }
      
      // Add subtle movement to the curve
      const points = curve.getPoints(50).map(point => {
        const noise = new THREE.Vector3(
          Math.sin(t + point.x) * 0.03,
          Math.cos(t + point.y) * 0.03,
          Math.sin(t + point.z) * 0.03
        )
        return point.add(noise)
      })
      
      lineRef.current.geometry.setFromPoints(points)
    }
  })

  return (
    <group>
      {/* Main synapse line */}
      <line
        ref={lineRef}
        geometry={new THREE.BufferGeometry().setFromPoints(curve.getPoints(50))}
        material={synapseMaterial}
      />
      
      {/* Energy particles */}
      <points
        ref={particlesRef}
        geometry={particleGeometry}
        material={particleMaterial}
      />
      
      {/* Connection points with enhanced glow */}
      <mesh position={new THREE.Vector3(...start)}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      
      <mesh position={new THREE.Vector3(...end)}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  )
}

export default Edge
